import { ChevronsUpDown } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@nucleus-commerce/admin-kit/date-time';
import { MoneyInput, TagInput } from '@nucleus-commerce/admin-kit/forms';
import type { RuleConditionInputOperator } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { isListOperator, type RuleFieldDef, type RuleFieldOption } from '../schemas/rule-fields';

export interface RuleValueInputProps {
  /** Undefined until a field is picked; the input is then disabled. */
  field: RuleFieldDef | undefined;
  operator: RuleConditionInputOperator;
  /** A string (money in minor units, dates as YYYY-MM-DD) or a list for in / not_in. */
  value: string | string[];
  onChange: (value: string | string[]) => void;
  currency: string;
  id?: string;
  'aria-invalid'?: boolean;
  disabled?: boolean;
}

function EnumMultiSelect({
  options,
  value,
  onChange,
  label,
  id,
  invalid,
  disabled,
}: {
  options: RuleFieldOption[];
  value: string[];
  onChange: (value: string[]) => void;
  label: string;
  id?: string;
  invalid?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation('productRules');
  const idPrefix = useId();
  const toggle = (option: string, checked: boolean) =>
    onChange(checked ? [...value, option] : value.filter((v) => v !== option));
  const summary =
    value.length === 0
      ? t('rules.pickValues', { count: 0 })
      : value.length <= 2
        ? value.map((v) => options.find((o) => o.value === v)?.label ?? v).join(', ')
        : t('rules.pickValues', { count: value.length });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-haspopup="listbox"
          aria-label={label}
          aria-invalid={invalid}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={value.length === 0 ? 'text-muted-foreground truncate' : 'truncate'}>
            {summary}
          </span>
          <ChevronsUpDown className="text-muted-foreground size-4 shrink-0" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-72 w-64 overflow-y-auto p-1">
        <ul className="flex flex-col">
          {options.map((option) => {
            const optionId = `${idPrefix}-${option.value}`;
            return (
              <li key={option.value}>
                <label
                  htmlFor={optionId}
                  className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
                >
                  <Checkbox
                    id={optionId}
                    checked={value.includes(option.value)}
                    onCheckedChange={(checked) => toggle(option.value, checked === true)}
                    aria-label={option.label}
                  />
                  <span className="truncate">{option.label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The value editor of one condition. What it renders follows the field's kind and the operator:
 * a select or multi-select for enums, money for prices, a date for created_at, yes/no for
 * booleans, tags for "any of" on free text, and a plain input otherwise.
 */
export function RuleValueInput({
  field,
  operator,
  value,
  onChange,
  currency,
  id,
  'aria-invalid': invalid,
  disabled,
}: RuleValueInputProps) {
  const { t } = useTranslation('productRules');
  const list = Array.isArray(value) ? value : [];
  const scalar = Array.isArray(value) ? '' : value;
  const label = t('rules.value');

  if (!field) {
    return <Input id={id} disabled placeholder={t('rules.pickField')} aria-label={label} />;
  }

  if (isListOperator(operator)) {
    if (field.kind === 'enum' && field.options) {
      return (
        <EnumMultiSelect
          id={id}
          options={field.options}
          value={list}
          onChange={onChange}
          label={label}
          invalid={invalid}
          disabled={disabled}
        />
      );
    }
    return (
      <TagInput
        id={id}
        value={list}
        onChange={onChange}
        placeholder={t('rules.valuesPlaceholder')}
        max={50}
        disabled={disabled}
        aria-invalid={invalid}
        removeLabel={(v) => t('rules.removeValue', { value: v })}
      />
    );
  }

  switch (field.kind) {
    case 'enum':
      return (
        <Select value={scalar} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger id={id} className="w-full" aria-label={label} aria-invalid={invalid}>
            <SelectValue placeholder={t('rules.pickValue')} />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'boolean':
      return (
        <Select value={scalar} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger id={id} className="w-full" aria-label={label} aria-invalid={invalid}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">{t('rules.boolean.true')}</SelectItem>
            <SelectItem value="false">{t('rules.boolean.false')}</SelectItem>
          </SelectContent>
        </Select>
      );
    case 'money':
      return (
        <MoneyInput
          id={id}
          currency={currency}
          value={scalar === '' ? null : { amount: Number(scalar), currency }}
          onChange={(money) => onChange(money ? String(money.amount) : '')}
          aria-label={label}
          aria-invalid={invalid}
          disabled={disabled}
        />
      );
    case 'date':
      return (
        <DatePicker
          id={id}
          value={scalar}
          onChange={onChange}
          aria-label={label}
          aria-invalid={invalid}
          disabled={disabled}
        />
      );
    case 'number':
      return (
        <Input
          id={id}
          inputMode="decimal"
          value={scalar}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          aria-invalid={invalid}
          disabled={disabled}
        />
      );
    case 'text':
    case 'tag':
      return (
        <Input
          id={id}
          value={scalar}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          aria-invalid={invalid}
          disabled={disabled}
        />
      );
  }
}
